import type {
  ConvertQuotationToInvoiceInput,
  CreateDocumentDTO,
  CreateMyDocumentPaymentInput,
} from "@/modules/quotation-invoice/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { myDocumentsApi } from "./my-documents-client";

export const myDocumentsKeys = {
  all: ["my-documents"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["my-documents", "list", q] as const,
  summary: () => ["my-documents", "summary"] as const,
  detail: (id: string) => ["my-documents", "detail", id] as const,
  documentTypes: (params: { limit?: number; includeInactive?: boolean }) =>
    ["my-documents", "document-types", params] as const,
};

export function useMyDocumentsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: myDocumentsKeys.list(q),
    queryFn: () => myDocumentsApi.list(q),
  });
}

export function useMyDocumentsSummaryQuery() {
  return useQuery({
    queryKey: myDocumentsKeys.summary(),
    queryFn: () => myDocumentsApi.summary(),
  });
}

export function useMyDocumentDetailQuery(id: string) {
  return useQuery({
    queryKey: myDocumentsKeys.detail(id),
    queryFn: () => myDocumentsApi.getDetail(id),
    enabled: !!id,
  });
}

export function useMyDocumentTypesQuery(
  params: { limit?: number; includeInactive?: boolean } = {},
) {
  return useQuery({
    queryKey: myDocumentsKeys.documentTypes(params),
    queryFn: () => myDocumentsApi.getDocumentTypes(params),
  });
}

export function useCreateMyDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDocumentDTO) => myDocumentsApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: myDocumentsKeys.all });
      toast.success("ບັນທຶກເອກະສານສຳເລັດແລ້ວ");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "ບັນທຶກບໍ່ສຳເລັດ");
    },
  });
}

export function useUpdateMyDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateDocumentDTO }) =>
      myDocumentsApi.update(id, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: myDocumentsKeys.all });
      queryClient.invalidateQueries({ queryKey: myDocumentsKeys.detail(id) });
      toast.success("ແກ້ໄຂເອກະສານສຳເລັດ");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "ແກ້ໄຂເອກະສານລົ້ມເຫຼວ");
    },
  });
}

export function useUpdateMyDocumentStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: "issued" | "cancelled";
    }) => myDocumentsApi.updateStatus(id, status),
    onSuccess: (_, { id, status }) => {
      queryClient.invalidateQueries({ queryKey: myDocumentsKeys.all });
      queryClient.invalidateQueries({ queryKey: myDocumentsKeys.detail(id) });
      const msg =
        status === "issued"
          ? "ອອກເອກະສານສຳເລັດ"
          : "ຍົກເລີກເອກະສານສຳເລັດ";
      toast.success(msg);
    },
    onError: (err: unknown) => {
      const shortfalls = (
        err as {
          code?: string;
          shortfalls?: Array<{
            productCode?: string | null;
            productName?: string | null;
            shortfall: number;
          }>;
        }
      ).shortfalls;
      if (shortfalls?.length) {
        const detail = shortfalls
          .map(
            (row) =>
              `${row.productCode ?? row.productName ?? "?"} (-${row.shortfall})`,
          )
          .join(", ");
        toast.error(`ສຕັອກບໍ່ພໍ: ${detail}`);
        return;
      }
      toast.error(err instanceof Error ? err.message : "ອັບເດດສະຖານະລົ້ມເຫຼວ");
    },
  });
}

export function useDeleteMyDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => myDocumentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: myDocumentsKeys.all });
      toast.success("ລຶບເອກະສານສຳເລັດ");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "ລຶບເອກະສານລົ້ມເຫຼວ");
    },
  });
}

export function useConvertMyQuotationToInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body?: ConvertQuotationToInvoiceInput;
    }) => myDocumentsApi.convertToInvoice(id, body),
    onSuccess: (_data, { id: quotationId }) => {
      queryClient.invalidateQueries({ queryKey: myDocumentsKeys.all });
      queryClient.invalidateQueries({
        queryKey: myDocumentsKeys.detail(quotationId),
      });
      toast.success(
        "ສ້າງໃບບິນງວດແລ້ວ — ກວດແລະແກ້ຍອດງວດໃນຮ່າງກ່ອນອອກເອກະສານ",
      );
    },
    onError: (err: unknown) => {
      toast.error(
        err instanceof Error ? err.message : "ບໍ່ສາມາດຕໍ່ເປັນໃບບິນ",
      );
    },
  });
}

export function useConvertMyQuotationToSalesOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => myDocumentsApi.convertToSalesOrder(id),
    onSuccess: (data, quotationId) => {
      queryClient.invalidateQueries({ queryKey: myDocumentsKeys.all });
      queryClient.invalidateQueries({
        queryKey: myDocumentsKeys.detail(quotationId),
      });
      toast.success(`ສ້າງຄຳສັ່ງຂາຍ ${data.orderNo} ແລ້ວ`);
    },
    onError: (err: unknown) => {
      toast.error(
        err instanceof Error ? err.message : "ບໍ່ສາມາດແປງເປັນຄຳສັ່ງຂາຍ",
      );
    },
  });
}

export function useConvertMyInvoiceToReceipt(documentId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paymentId }: { id: string; paymentId: string }) =>
      myDocumentsApi.convertToReceipt(id, paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: myDocumentsKeys.all });
      if (documentId) {
        queryClient.invalidateQueries({
          queryKey: myDocumentsKeys.detail(documentId),
        });
      }
      toast.success("ສ້າງໃບຮັບເງິນຈາກໃບບິນແລ້ວ");
    },
    onError: (err: unknown) => {
      toast.error(
        err instanceof Error ? err.message : "ບໍ່ສາມາດຕໍ່ເປັນໃບຮັບເງິນ",
      );
    },
  });
}

export function useRecordMyDocumentPayment(documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMyDocumentPaymentInput) =>
      myDocumentsApi.createDocumentPayment(documentId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: myDocumentsKeys.detail(documentId),
      });
      queryClient.invalidateQueries({ queryKey: myDocumentsKeys.all });
      toast.success("ບັນທຶກການຊຳລະສຳເລັດ");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "ບັນທຶກການຊຳລະລົ້ມເຫຼວ");
    },
  });
}
